import React, { useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation, NavLink } from 'react-router-dom'
import ShipmentsList from 'pages/ShipmentsList'
import ShipmentDetail from 'pages/ShipmentDetail'
import DocumentDetail from 'pages/DocumentDetail'
import Dashboard from 'pages/Dashboard'
import AdminDashboard from 'pages/AdminDashboard'
import AdminUsers from 'pages/AdminUsers'
import AdminTariffs from 'pages/AdminTariffs'
import Login from 'pages/Login'
import Signup from 'pages/Signup'
import VerifyEmail from 'pages/VerifyEmail'
import ForgotPassword from 'pages/ForgotPassword'
import ProtectedRoute from 'components/ProtectedRoute'
import LanguageSwitcher from 'components/LanguageSwitcher'
import { getUser, logout, isAuthenticated } from 'services/auth.service'
import { useI18n } from 'i18n'

const navLinkClass = ({ isActive }) =>
  `rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'}`

const mobileNavLinkClass = ({ isActive }) =>
  `block rounded-lg px-3 py-2 text-sm font-medium ${isActive ? 'bg-white/10 text-white' : 'text-white/75 hover:bg-white/5 hover:text-white'}`

const NavLinks = ({ isAdmin, linkClass }) => {
  const { t } = useI18n()
  return (
    <>
      <NavLink to='/' end className={linkClass}>{t('nav.dashboard')}</NavLink>
      {isAdmin
        ? (
          <>
            <NavLink to='/admin/users' className={linkClass}>{t('nav.users')}</NavLink>
            <NavLink to='/admin/tariffs' className={linkClass}>{t('nav.tariffs')}</NavLink>
          </>
          )
        : <NavLink to='/shipments' className={linkClass}>{t('nav.shipments')}</NavLink>}
    </>
  )
}

// Full bar from `lg`; below it, the nav, language and account collapse into a menu panel.
const Header = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useI18n()
  const [menuOpen, setMenuOpen] = useState(false)
  const headerRef = useRef(null)
  const user = getUser()
  const isAdmin = user?.role === 'system_admin'
  const signedIn = isAuthenticated()

  useEffect(() => { setMenuOpen(false) }, [pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (event) => { if (event.key === 'Escape') setMenuOpen(false) }
    const onPointerDown = (event) => { if (!headerRef.current?.contains(event.target)) setMenuOpen(false) }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [menuOpen])

  const onLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }
  const userLabel = (
    <>
      {user?.firstName || user?.email} · {t(`roles.${user?.role}`)}
      {user?.clientName ? ` · ${user.clientName}` : ''}
    </>
  )
  return (
    <header ref={headerRef} className='sticky top-0 z-20 border-b border-white/10 bg-ink text-white shadow-sm'>
      <div className='mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:py-4'>
        <div className='flex min-w-0 items-center gap-6'>
          <Link to='/' className='flex shrink-0 items-center gap-2 text-lg font-bold'>
            <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt='' width='32' height='32' className='h-8 w-8 rounded-[7.5px] ring-1 ring-white/15' />
            <span className='flex flex-col leading-none'>
              {t('app.name')}
              <span className='mt-1 hidden text-[11px] font-medium text-white/60 sm:block'>{t('app.descriptor')}</span>
            </span>
          </Link>
          <nav className='hidden items-center gap-1 lg:flex'>
            <NavLinks isAdmin={isAdmin} linkClass={navLinkClass} />
          </nav>
        </div>
        {signedIn && (
          <div className='hidden items-center gap-4 text-sm lg:flex'>
            <LanguageSwitcher />
            <span className='truncate text-white/70'>{userLabel}</span>
            <button onClick={onLogout} className='shrink-0 rounded-lg px-3 py-1.5 font-semibold ring-1 ring-white/25 hover:bg-white/10'>{t('nav.signOut')}</button>
          </div>
        )}
        <button type='button' onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen} aria-controls='mobile-menu' aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          className='-mr-2 rounded-lg p-2 hover:bg-white/10 lg:hidden'>
          <svg aria-hidden viewBox='0 0 24 24' className='h-6 w-6' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
            {menuOpen ? <path d='M6 6l12 12M18 6L6 18' /> : <path d='M4 7h16M4 12h16M4 17h16' />}
          </svg>
        </button>
      </div>
      {menuOpen && (
        <div id='mobile-menu' className='border-t border-white/10 px-4 pb-4 pt-2 sm:px-6 lg:hidden'>
          <nav className='flex flex-col gap-1'>
            <NavLinks isAdmin={isAdmin} linkClass={mobileNavLinkClass} />
          </nav>
          {signedIn && (
            <div className='mt-3 space-y-3 border-t border-white/10 pt-3 text-sm'>
              <p className='px-3 text-white/70'>{userLabel}</p>
              <div className='flex items-center justify-between gap-3 px-3'>
                <LanguageSwitcher />
                <button onClick={onLogout} className='rounded-lg px-3 py-1.5 font-semibold ring-1 ring-white/25 hover:bg-white/10'>{t('nav.signOut')}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  )
}

const Shell = ({ children }) => (
  <div className='min-h-screen'>
    <Header />
    <main className='mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8'>{children}</main>
  </div>
)

// The landing dashboard depends on the role.
const Home = () => (getUser()?.role === 'system_admin' ? <AdminDashboard /> : <Dashboard />)

const protectedShell = (element, roles) => (
  <ProtectedRoute roles={roles}><Shell>{element}</Shell></ProtectedRoute>
)

const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path='/login' element={<Login />} />
      <Route path='/signup' element={<Signup />} />
      <Route path='/verify-email' element={<VerifyEmail />} />
      <Route path='/forgot-password' element={<ForgotPassword />} />
      <Route path='/' element={protectedShell(<Home />)} />
      <Route path='/shipments' element={protectedShell(<ShipmentsList />, ['client'])} />
      <Route path='/shipments/:shipmentId' element={protectedShell(<ShipmentDetail />, ['client'])} />
      <Route path='/documents/:documentId' element={protectedShell(<DocumentDetail />, ['client'])} />
      <Route path='/admin/users' element={protectedShell(<AdminUsers />, ['system_admin'])} />
      <Route path='/admin/tariffs' element={protectedShell(<AdminTariffs />, ['system_admin'])} />
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  </BrowserRouter>
)

export default App
